import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../api/axios'
import { Alert } from '../../../components/ui/Alert'
import { Modal } from '../../../components/ui/Modal'
import { FormField, inputClass, selectClass } from '../../../components/ui/FormField'
import type { DivisaoIpsc } from '../divisoes'
import { btnPrimario, btnSecundario, cartao, erroApi, td, th } from './comum'

// Cadastro das divisoes IPSC (lista escolhida pelo atirador na inscricao)

const PF: Record<string, string> = { ambos: 'Major ou Minor', major: 'Só Major', minor: 'Só Minor' }
type Form = { nome: string; sigla: string; ordem: string; power_factor_permitido: 'ambos' | 'major' | 'minor'; ativo: boolean }

export function AbaDivisoes() {
  const qc = useQueryClient()
  const [editando, setEditando] = useState<DivisaoIpsc | 'nova' | null>(null)
  const [form, setForm] = useState<Form>({ nome: '', sigla: '', ordem: '0', power_factor_permitido: 'ambos', ativo: true })
  const [alerta, setAlerta] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const { data: divisoes = [] } = useQuery({
    queryKey: ['ipsc-divisoes-todas'],
    queryFn: () => api.get<{ data: DivisaoIpsc[] }>('/ipsc/divisoes', { params: { todas: 'true' } }).then((r) => r.data.data),
  })
  const salvar = useMutation({
    mutationFn: (dados: object) =>
      editando && editando !== 'nova' ? api.put(`/ipsc/divisoes/${editando.iddivisao}`, dados) : api.post('/ipsc/divisoes', dados),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ipsc-divisoes-todas'] })
      qc.invalidateQueries({ queryKey: ['ipsc-divisoes-publicas'] })
      setEditando(null)
      setAlerta({ type: 'success', message: 'Divisão salva' })
    },
    onError: (e) => setAlerta({ type: 'error', message: erroApi(e, 'Erro ao salvar a divisão') }),
  })

  function abrir(d: DivisaoIpsc | 'nova') {
    setEditando(d)
    setForm(d === 'nova'
      ? { nome: '', sigla: '', ordem: String((divisoes.at(-1)?.ordem ?? 0) + 1), power_factor_permitido: 'ambos', ativo: true }
      : { nome: d.nome, sigla: d.sigla ?? '', ordem: String(d.ordem), power_factor_permitido: d.power_factor_permitido, ativo: d.ativo })
  }

  return (
    <div className="space-y-3">
      {alerta && <Alert type={alerta.type} message={alerta.message} onClose={() => setAlerta(null)} />}
      <div className="flex justify-between items-center">
        <p className="text-sm text-compumaq-textoSuave">
          Lista que o atirador escolhe na inscrição de modalidade IPSC. Divisão inativa não aparece mais na inscrição.
        </p>
        <button className={btnPrimario} onClick={() => abrir('nova')}>+ Nova divisão</button>
      </div>
      <div className={`${cartao} overflow-x-auto`}>
        <table className="w-full text-sm">
          <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25">
            <tr><th className={th}>Ordem</th><th className={th}>Divisão</th><th className={th}>Sigla</th><th className={th}>Power Factor</th><th className={th}>Situação</th><th className={th}></th></tr>
          </thead>
          <tbody className="divide-y divide-compumaq-dourado/25">
            {divisoes.map((d) => (
              <tr key={d.iddivisao} className={d.ativo ? '' : 'opacity-50'}>
                <td className={td}>{d.ordem}</td>
                <td className={`${td} font-medium text-compumaq-texto`}>{d.nome}</td>
                <td className={td}>{d.sigla ?? '—'}</td>
                <td className={td}>{PF[d.power_factor_permitido]}</td>
                <td className={td}>{d.ativo ? 'Ativa' : 'Inativa'}</td>
                <td className={`${td} text-right`}>
                  <button className="text-xs text-compumaq-doradoClaro font-medium" onClick={() => abrir(d)}>Editar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={editando !== null} onClose={() => setEditando(null)} title={editando === 'nova' ? 'Nova divisão' : 'Editar divisão'}>
        <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); salvar.mutate({ ...form, ordem: Number(form.ordem) || 0, sigla: form.sigla || null }) }}>
          <FormField label="Nome" required>
            <input className={inputClass} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} aria-label="Nome da divisão" />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Sigla"><input className={inputClass} value={form.sigla} onChange={(e) => setForm({ ...form, sigla: e.target.value })} /></FormField>
            <FormField label="Ordem na lista"><input className={inputClass} type="number" value={form.ordem} onChange={(e) => setForm({ ...form, ordem: e.target.value })} /></FormField>
          </div>
          <FormField label="Power Factor aceito" hint="Production, Production Optics e CCP são só Minor na regra IPSC">
            <select className={selectClass} value={form.power_factor_permitido} aria-label="Power Factor aceito"
              onChange={(e) => setForm({ ...form, power_factor_permitido: e.target.value as Form['power_factor_permitido'] })}>
              <option value="ambos">Major ou Minor</option>
              <option value="minor">Só Minor</option>
              <option value="major">Só Major</option>
            </select>
          </FormField>
          <label className="flex items-center gap-2 text-sm text-compumaq-texto">
            <input type="checkbox" checked={form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} /> Ativa
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className={btnSecundario} onClick={() => setEditando(null)}>Cancelar</button>
            <button type="submit" className={btnPrimario} disabled={salvar.isPending}>{salvar.isPending ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
